import React, { useCallback, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import AnchoredPopover from './AnchoredPopover';
import DateTimePicker from '../../../components/DateTimePicker';
import LocationLookupField from '../../order-management/components/LocationLookupField';
import type { LocationLookupRow } from '../../order-management/fixtures';
import ordersService, { GroundedInfoItem } from '../../../services/orders.service';
import { useToast } from '../../../contexts/ToastContext';
import { parseMdYDate } from '../../../utils/dateUtils';

/** Local-time YYYY-MM-DD (avoids the UTC drift of Date.toISOString). */
const toIsoDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** MM/DD/YYYY (display string) -> YYYY-MM-DD, or '' when blank/unparseable. */
const mdyToIso = (s: string): string => {
  const d = parseMdYDate(s);
  return d ? toIsoDate(d) : '';
};

/** Inclusive day count between two dates, or null when either is missing / end < start. */
const inclusiveDays = (start: Date | null, end: Date | null): number | null => {
  if (!start || !end) return null;
  const ms = end.getTime() - start.getTime();
  if (ms < 0) return null;
  return Math.round(ms / 86_400_000) + 1;
};

interface Props {
  isOpen: boolean;
  pronumber: number;
  rows: GroundedInfoItem[];
  /** Trigger rect, for anchoring the popover. */
  buttonRect: DOMRect | null;
  onClose: () => void;
  /** Called after any add/edit/delete so the parent can re-pull groundedInfo. */
  onChanged: () => void;
}

const dpClass = 'w-full [&_div]:!min-w-0';

/**
 * Grounded Dates editor for Pro Details — a Storage-Cleared-style anchored
 * popover. Ports V1 main.pl editgroundedinfo / savegroundedinfo: lists the
 * PRO's cms.groundeddates rows and lets the user add a new grounded period
 * (location + required start + optional end), edit an existing row's location /
 * start / end, or soft-delete a row. 'days' is always derived, never entered.
 *
 * The add section is at the TOP (not a bottom "New:" footer like Storage
 * Cleared) so the location typeahead's dropdown opens downward into the list
 * area rather than clipping past the popover's bottom edge.
 */
const GroundedDatesPopover: React.FC<Props> = ({ isOpen, pronumber, rows, buttonRect, onClose, onChanged }) => {
  const { showToast } = useToast();

  const [addName, setAddName] = useState('');
  const [addCode, setAddCode] = useState(0);
  const [addStart, setAddStart] = useState<Date | null>(null);
  const [addEnd, setAddEnd] = useState<Date | null>(null);
  const [adding, setAdding] = useState(false);

  const resetAddRow = () => {
    setAddName('');
    setAddCode(0);
    setAddStart(null);
    setAddEnd(null);
  };

  const handleAdd = useCallback(async () => {
    if (adding) return;
    if (addCode <= 0) {
      showToast({ type: 'error', message: 'Pick a location from the list' });
      return;
    }
    if (!addStart) {
      showToast({ type: 'error', message: 'Start date is required' });
      return;
    }
    if (addEnd && inclusiveDays(addStart, addEnd) == null) {
      showToast({ type: 'error', message: 'End date cannot be before the start date' });
      return;
    }
    setAdding(true);
    try {
      await ordersService.addGroundedDate(pronumber, {
        locrecno: addCode,
        locName: addName,
        startDate: toIsoDate(addStart),
        endDate: addEnd ? toIsoDate(addEnd) : undefined,
      });
      showToast({ type: 'success', message: 'Grounded date added' });
      resetAddRow();
      onChanged();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showToast({ type: 'error', message: typeof detail === 'string' ? detail : 'Failed to add grounded date' });
    } finally {
      setAdding(false);
    }
  }, [adding, addCode, addStart, addEnd, addName, pronumber, showToast, onChanged]);

  const addDays = inclusiveDays(addStart, addEnd);

  return (
    <AnchoredPopover
      isOpen={isOpen}
      buttonRect={buttonRect}
      onClose={onClose}
      title="Grounded Dates"
      width={360}
      maxHeight={460}
    >
      {(containerEl) => (
        <>
          {/* Add section (top so the location dropdown opens into the list). */}
          <div className="px-3 py-2 border-b border-slate-200 dark:border-gray-700 shrink-0">
            <div className="text-[0.65625rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-gray-400 mb-1">
              Add grounded date
            </div>
            <LocationLookupField
              value={addName}
              linkedCode={addCode}
              onType={(name) => { setAddName(name); setAddCode(0); }}
              onPick={(r: LocationLookupRow) => { setAddName(r.lname ?? ''); setAddCode(r.locationcode); }}
              ariaLabel="Grounded location"
              placeholder="Search locations…"
            />
            <div className="flex items-center gap-1.5 mt-1.5">
              <div className="flex-1 min-w-0">
                <DateTimePicker
                  mode="date"
                  selectionType="single"
                  inputVariant="compact"
                  labelType="none"
                  autoApply
                  usePortal
                  portalContainer={containerEl}
                  className={dpClass}
                  value={addStart}
                  onChange={(v) => setAddStart(v instanceof Date ? v : null)}
                  placeholder="Start *"
                />
              </div>
              <span className="text-slate-400 dark:text-gray-500">–</span>
              <div className="flex-1 min-w-0">
                <DateTimePicker
                  mode="date"
                  selectionType="single"
                  inputVariant="compact"
                  labelType="none"
                  autoApply
                  usePortal
                  portalContainer={containerEl}
                  className={dpClass}
                  value={addEnd}
                  onChange={(v) => setAddEnd(v instanceof Date ? v : null)}
                  placeholder="End"
                />
              </div>
              <span className="w-7 text-right text-[0.6875rem] text-slate-500 dark:text-gray-400">
                {addDays != null ? addDays : '—'}
              </span>
              <button
                type="button"
                onClick={() => void handleAdd()}
                disabled={addCode <= 0 || !addStart || adding}
                className="px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {adding ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>

          {/* Existing rows. */}
          <div className="overflow-y-auto flex-1 px-3 py-2">
            {rows.length === 0 ? (
              <p className="py-2 text-[0.75rem] italic text-slate-400 dark:text-gray-500">
                No grounded dates on this Pro.
              </p>
            ) : (
              <div className="space-y-2">
                {rows.map((row) => (
                  <GroundedRow
                    key={`${row.recno}:${row.locRecno}:${row.startDate}:${row.endDate}`}
                    pronumber={pronumber}
                    row={row}
                    portalContainer={containerEl}
                    onChanged={onChanged}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </AnchoredPopover>
  );
};

interface RowProps {
  pronumber: number;
  row: GroundedInfoItem;
  portalContainer: HTMLElement | null;
  onChanged: () => void;
}

/**
 * One editable grounded-dates row (compact stacked card). Local draft state
 * seeds from `row`; the parent remounts this component (via a data-encoded key)
 * whenever the row's persisted values change, so a save cleanly resets the draft.
 */
const GroundedRow: React.FC<RowProps> = ({ pronumber, row, portalContainer, onChanged }) => {
  const { showToast } = useToast();
  const [locName, setLocName] = useState(row.locName);
  const [locCode, setLocCode] = useState(row.locRecno);
  const [start, setStart] = useState<Date | null>(parseMdYDate(row.startDate));
  const [end, setEnd] = useState<Date | null>(parseMdYDate(row.endDate));
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const origStartIso = mdyToIso(row.startDate);
  const origEndIso = mdyToIso(row.endDate);
  const curStartIso = start ? toIsoDate(start) : '';
  const curEndIso = end ? toIsoDate(end) : '';

  // A free-typed name without picking (code 0) can't be persisted (needs a
  // locrecno), so only a picked, changed location counts as dirty.
  const locDirty = locCode > 0 && locCode !== row.locRecno;
  const startDirty = curStartIso !== origStartIso;
  const endDirty = curEndIso !== origEndIso;
  const dirty = locDirty || startDirty || endDirty;

  const handleSave = useCallback(async () => {
    if (saving || !dirty) return;
    if (!start) {
      showToast({ type: 'error', message: 'Start date is required' });
      return;
    }
    if (end && inclusiveDays(start, end) == null) {
      showToast({ type: 'error', message: 'End date cannot be before the start date' });
      return;
    }
    const payload: {
      locrecno?: number;
      locName?: string;
      startDate?: string;
      endDate?: string;
    } = {};
    if (locDirty) {
      payload.locrecno = locCode;
      payload.locName = locName;
    }
    if (startDirty) payload.startDate = curStartIso;
    if (endDirty) payload.endDate = curEndIso; // '' clears (reopens) the period

    setSaving(true);
    try {
      await ordersService.updateGroundedDate(pronumber, row.recno, payload);
      showToast({ type: 'success', message: 'Grounded date updated' });
      onChanged();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showToast({ type: 'error', message: typeof detail === 'string' ? detail : 'Failed to update grounded date' });
    } finally {
      setSaving(false);
    }
  }, [saving, dirty, start, end, locDirty, startDirty, endDirty, locCode, locName, curStartIso, curEndIso, pronumber, row.recno, showToast, onChanged]);

  const handleDelete = useCallback(async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await ordersService.deleteGroundedDate(pronumber, row.recno);
      showToast({ type: 'success', message: 'Grounded date deleted' });
      onChanged();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showToast({ type: 'error', message: typeof detail === 'string' ? detail : 'Failed to delete grounded date' });
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }, [deleting, pronumber, row.recno, showToast, onChanged]);

  const days = inclusiveDays(start, end);

  return (
    <div className="border border-slate-200 dark:border-gray-700 rounded p-1.5">
      <LocationLookupField
        value={locName}
        linkedCode={locCode}
        onType={(name) => { setLocName(name); setLocCode(0); }}
        onPick={(r: LocationLookupRow) => { setLocName(r.lname ?? ''); setLocCode(r.locationcode); }}
        ariaLabel="Grounded location"
        placeholder="Search locations…"
      />
      <div className="flex items-center gap-1.5 mt-1.5">
        <div className="flex-1 min-w-0">
          <DateTimePicker
            mode="date"
            selectionType="single"
            inputVariant="compact"
            labelType="none"
            autoApply
            usePortal
            portalContainer={portalContainer}
            className={dpClass}
            value={start}
            onChange={(v) => setStart(v instanceof Date ? v : null)}
            placeholder="Start *"
          />
        </div>
        <span className="text-slate-400 dark:text-gray-500">–</span>
        <div className="flex-1 min-w-0">
          <DateTimePicker
            mode="date"
            selectionType="single"
            inputVariant="compact"
            labelType="none"
            autoApply
            usePortal
            portalContainer={portalContainer}
            className={dpClass}
            value={end}
            onChange={(v) => setEnd(v instanceof Date ? v : null)}
            placeholder="End"
          />
        </div>
        <span className="w-7 text-right text-[0.6875rem] text-slate-500 dark:text-gray-400">
          {days != null ? days : <span className="italic">open</span>}
        </span>
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          disabled={deleting}
          className="text-slate-400 hover:text-red-600 disabled:opacity-50"
          aria-label="Delete grounded date"
          title="Delete grounded date"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      {(dirty || confirmDelete) && (
        <div className="mt-1.5 flex items-center justify-end gap-2">
          {confirmDelete ? (
            <>
              <span className="text-[0.71875rem] text-slate-600 dark:text-gray-300 mr-auto">Delete this grounded date?</span>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-gray-200 bg-slate-100 dark:bg-gray-600 rounded hover:bg-slate-200 dark:hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={deleting}
                className="px-2 py-0.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-2.5 py-0.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default GroundedDatesPopover;
